import { InfoSectionData } from './FieldInfoButton';

// Main Referee Page Overview
export const mainRefereeOverviewInfo: InfoSectionData[] = [
  {
    title: 'Main Referee Role',
    description: `
      As the <strong>Main Referee</strong>, you have special privileges to manage your assigned field.
      <br/><br/>
      <strong>Your responsibilities include:</strong><br/>
      • Managing field maps and game nodes<br/>
      • Creating and publishing field news<br/>
      • Viewing and managing games<br/>
      • Managing other referees assigned to your field
    `,
  },
  {
    title: 'What You Can Do',
    fields: [
      {
        name: 'Maps & Nodes',
        description: 'Create and edit maps, manage game nodes and the Main Node (Office Gateway).',
      },
      {
        name: 'News',
        description: 'Create news articles, edit drafts, publish or unpublish news for your field.',
      },
      {
        name: 'Games',
        description: 'View all games scheduled and played at your field.',
      },
      {
        name: 'Referees',
        description: 'View all referees assigned to your field, activate, deactivate, or block them.',
      },
    ],
  },
  {
    title: 'Limitations',
    description: `
      <strong>As Main Referee you cannot:</strong><br/>
      • Edit field basic information (name, address, logo)<br/>
      • Delete the field<br/>
      • Invite or remove referees from the field<br/>
      • Assign or change the Main Referee role<br/><br/>
      These actions require <strong>Field Owner</strong> access.
    `,
  },
];

// Maps & Nodes Tab Help (for Main Referee context)
export const mainRefereeMapsInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Maps & Nodes</strong> allows you to manage your field's playing areas and the physical devices (nodes) installed on them.
      <br/><br/>
      <strong>Main Node</strong> - Your field's internet gateway (<strong>InternetNode</strong>) that connects all other nodes to the cloud.
      <br/>
      <strong>Maps</strong> - Represent different playing zones or arenas at your field.
      <br/>
      <strong>Nodes</strong> - Physical <strong>LoRa mesh network</strong> devices that enable communication and game tracking across your field.
    `,
  },
  {
    title: 'Main Node (Office Gateway)',
    description: `
      The <strong>Main Node</strong> is a special <strong>InternetNode</strong> that acts as your field's primary gateway to the <strong>NovaWarfare cloud</strong>.
      <br/><br/>
      • It should be placed in your <strong>office or reception area</strong> near a WiFi router<br/>
      • It connects all map nodes to the internet for <strong>real-time game tracking</strong><br/>
      • Only <strong>one Main Node per field</strong> is supported<br/>
      • The Main Node <strong>cannot be assigned to any map</strong> - it operates independently<br/>
      • You need the <strong>Node Code</strong> from your purchased InternetNode device to set it up
    `,
  },
  {
    title: 'Creating Maps',
    fields: [
      {
        name: 'Map Name',
        description: 'A clear name for your playing area (e.g., <strong>"Forest Arena"</strong>, <strong>"Urban Zone"</strong>, <strong>"Speedball Field"</strong>).',
      },
      {
        name: 'Description',
        description: 'Optional details about the map - terrain type, size, special features, or any notes for players and staff.',
      },
      {
        name: 'Schema Image',
        description: 'Upload a <strong>layout diagram</strong> of your playing area showing key positions, spawn points, and objectives.',
      },
      {
        name: 'Map Photos',
        description: 'Upload up to <strong>5 photos</strong> of the actual playing area to help players understand the terrain.',
      },
      {
        name: 'YouTube Videos',
        description: 'Add YouTube video links for <strong>gameplay footage</strong> or <strong>map walkthroughs</strong>.',
      },
    ],
  },
  {
    title: 'Managing Nodes',
    fields: [
      {
        name: 'Node Code',
        description: 'The unique identifier printed on your physical device. Enter this to find and assign your node to a map.',
      },
      {
        name: 'Position Description',
        description: 'A text description of where the node is <strong>physically located</strong>.<br/><br/>' +
          '<strong>Examples:</strong><br/>' +
          '• "North entrance, behind the large oak tree"<br/>' +
          '• "Building A, second floor, left window"<br/>' +
          '• "Center of the speedball field, bunker #3"',
      },
      {
        name: 'Node Types',
        description: '<strong>InternetNode</strong> - Main gateway (Office Node only, cannot be on maps).<br/>' +
          '<strong>MapNode</strong> - Standard game device for objectives.',
      },
    ],
  },
  {
    title: 'Map Actions',
    fields: [
      {
        name: 'Edit',
        description: 'Update map name, description, images, and YouTube videos.',
      },
      {
        name: 'Nodes',
        description: 'Assign or remove physical LoRa nodes to this map using their Node Codes.',
      },
      {
        name: 'Schema Editor',
        description: 'Open the visual map editor to draw your field layout, place buildings, barriers, zones, and position nodes on an interactive canvas. You can also create <strong>Mission Templates</strong> with pre-configured device positions for quick game setup.',
      },
      {
        name: 'Delete',
        description: 'Permanently remove the map. This will also unassign all nodes from this map.',
      },
    ],
  },
];

// News Tab Help
export const mainRefereeNewsInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      Create and manage <strong>news articles</strong> for your field. News appears on your field's public page and keeps players informed about updates, events, and announcements.
    `,
  },
  {
    title: 'Creating News',
    fields: [
      {
        name: 'Title',
        description: 'A clear, attention-grabbing headline for your news article (required).',
      },
      {
        name: 'Content',
        description: 'The main body of your news article. You can use <strong>formatting</strong> to make it more readable.',
      },
      {
        name: 'Image',
        description: 'Upload an optional cover image for your news article. <strong>Recommended size:</strong> 1200x630 pixels.',
      },
      {
        name: 'Priority',
        description: 'Mark important news as <strong>Priority</strong> to highlight it at the top of the news list.',
      },
    ],
  },
  {
    title: 'Publishing',
    description: `
      <strong>Draft</strong> - News is saved but not visible to the public. Use this to prepare articles before publishing.<br/><br/>
      <strong>Published</strong> - News is visible on your field's public page. Players can see it immediately.<br/><br/>
      You can <strong>unpublish</strong> news at any time to hide it from the public without deleting it.
    `,
  },
  {
    title: 'Best Practices',
    description: `
      • Use <strong>clear titles</strong> that summarize the news<br/>
      • Add <strong>images</strong> to make news more engaging<br/>
      • Mark time-sensitive announcements as <strong>Priority</strong><br/>
      • Keep content <strong>concise and informative</strong><br/>
      • <strong>Unpublish</strong> outdated news instead of deleting it
    `,
  },
];

// Games Tab Help
export const mainRefereeGamesInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      View all games scheduled and played at your field. This section shows game history, upcoming games, and their status.
    `,
  },
  {
    title: 'Game Types',
    fields: [
      {
        name: 'Laser Tag',
        description: 'Standard laser tag games with hit detection and scoring.',
      },
      {
        name: 'Airsoft',
        description: 'Airsoft games with objective-based gameplay.',
      },
      {
        name: 'Paintball',
        description: 'Paintball games with various game modes.',
      },
      {
        name: 'Nerf',
        description: 'Nerf battles for casual and family-friendly play.',
      },
    ],
  },
  {
    title: 'Game Status',
    description: `
      <strong>Scheduled</strong> - Game is planned for a future date<br/>
      <strong>In Progress</strong> - Game is currently being played<br/>
      <strong>Completed</strong> - Game has finished with recorded results<br/>
      <strong>Cancelled</strong> - Game was cancelled before starting
    `,
  },
  {
    title: 'Viewing Games',
    description: `
      • Use <strong>date filters</strong> to find games on specific days<br/>
      • Switch between <strong>game types</strong> using the tabs<br/>
      • Click on a game to see <strong>detailed information</strong><br/>
      • View <strong>participant lists</strong> and <strong>scores</strong> for completed games
    `,
  },
];

// Referees Tab Help
export const mainRefereeRefereesInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      Manage the referees assigned to your field. As Main Referee, you can <strong>activate</strong>, <strong>deactivate</strong>, or <strong>block</strong> other referees.
    `,
  },
  {
    title: 'Referee Status',
    fields: [
      {
        name: 'Active',
        description: 'Referee can work at the field, manage games, and access field resources. <span style="color: #4caf50;">Green indicator.</span>',
      },
      {
        name: 'Pending',
        description: 'Referee is deactivated and cannot work at the field. Use this for temporary breaks. <span style="color: #ffc107;">Yellow indicator.</span>',
      },
      {
        name: 'Blocked',
        description: 'Referee is blocked from accessing the field. Use this for disciplinary actions. <span style="color: #f44336;">Red indicator.</span>',
      },
    ],
  },
  {
    title: 'Managing Referees',
    description: `
      <strong>Activate</strong> - Allow a pending/blocked referee to work at the field<br/>
      <strong>Deactivate</strong> - Temporarily remove a referee's access (sets status to Pending)<br/>
      <strong>Block</strong> - Prevent a referee from accessing the field entirely<br/><br/>
      <strong>Note:</strong> You cannot change your own status.
    `,
  },
  {
    title: 'Limitations',
    description: `
      As Main Referee, you <strong>cannot</strong>:<br/>
      • Invite new referees to the field<br/>
      • Remove referees from the field<br/>
      • Change who is the Main Referee<br/><br/>
      These actions require <strong>Field Owner</strong> access.
    `,
  },
];
